import { Button } from "@/components/ui/baseUi/button"
import { UserCell, UserCellSkeleton } from "@/components/ui/layout/table/userCell"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useUser } from "@/modules/users/hooks/useUser"
import backgroundBenchmark from "@images/backgroundBenchmark.png"
import errorInvitationShareCoach from "@images/error/errorInvitationShareCoach.svg"
import InvitationIcon from "@images/invitationIcon.svg"
import { ClockOutlineIcon } from "@images/svgIcons/clockOutlineIcon"
import { MessagesIcon } from "@images/svgIcons/messagesIcon"
import { CorrectIcon } from "@images/svgIcons/status/correctIcon"
import { ErrorIcon } from "@images/svgIcons/status/errorIcon"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { ExploreCard } from "@modules/explore/components/exploreCard/exploreCard"
import { ExploreProgramItemEntity, ExploreProProgramItemEntity } from "@modules/explore/entities/exploreEntity"
import { Path } from "@modules/navigation/routes"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { ProgramType, SharedCoachRequestAction } from "@modules/programs/types/programTypes"
import { SharedCoachRole } from "@modules/sharedCoach/gateway/sharedCoachDtos"
import { useSharedCoachRequest } from "@modules/sharedCoach/hooks/useSharedCoachActions"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { useNavigate } from "react-router"

type InvitationDetailsInfoProps = {
  className?: string
} & React.ComponentProps<"div">

export function InvitationDetailsInfo({ className, ...props }: InvitationDetailsInfoProps) {
  const user = useUser()
  const navigate = useNavigate()
  const { hideSidebar } = useSidebar()
  const { localStorageService } = useServicesContext()
  const programId = localStorageService.load(LocalStorageKeys.sharedCoachInvitation.programId, { value: "" })
  const { data: program, isLoading } = useProgram(programId.value)
  const { sharedCoach, isAuthor, isAcceptedSharedCoach } = useProgramRights(programId.value)
  const { isPending, requestSharedCoach } = useSharedCoachRequest()

  const openChatWithCoach = () => {
    if (program.owner) {
      hideSidebar()
      localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
      navigate(Path.Chat.Root, { state: { userToChatTo: program.owner.id } })
    }
  }

  const requestSharedCoachActions = async (action: SharedCoachRequestAction) => {
    if (user?.id && program) {
      await requestSharedCoach(program.id, action, user.id)
        .then(() => {
          toast.success(word(`program.sharedCoach.action.request.${action}.success`), {
            classNames: successToastClassName(),
          })
        })
        .catch(() => {
          toast.error(word(`program.sharedCoach.action.request.error`), {
            classNames: errorToastClassName(),
          })
        })
        .finally(() => {
          hideSidebar()
          localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
        })
    }
  }

  return (
    <div className={cn("pb-4", className)} {...props}>
      {/* HEADER */}
      <div className="relative flex h-60 overflow-hidden rounded-b-3xl bg-neutral-200 pl-1">
        <div className="mt-2 ml-1 flex h-full w-1/2 flex-col items-center justify-center">
          <div
            className="bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${InvitationIcon} ')`, width: "60px", height: "60px" }}
          ></div>
          {isLoading ? (
            <>
              <Skeleton className="mt-1 h-8 w-8/10 rounded-xl" />
              <Skeleton className="mt-4 h-4 w-4/10 rounded-xl" />
              <UserCellSkeleton className="mt-1 flex w-full flex-row justify-center" subtitle={true} />
            </>
          ) : program ? (
            <>
              <Text fontStyle="HS" className="mt-1 line-clamp-2 pl-2 text-textDefault-heading">
                {program.name}
              </Text>
              <Text fontStyle="BS" className="mt-2 text-textDefault-body">
                {word("invitationDetailsInfo.InvitationBy")}
              </Text>
              <UserCell
                member={{
                  avatar: program.owner?.avatar ?? undefined,
                  username: program.owner?.username,
                  firstname: program.owner?.firstname ?? "",
                  lastname: program.owner?.lastname ?? "",
                }}
                subtitle={program.owner?.username ? "@" + program.owner.username : ""}
                className="mt-1 flex w-full cursor-pointer gap-2"
              />
            </>
          ) : null}
        </div>
        <div
          className="h-full w-1/2 bg-cover bg-right bg-no-repeat"
          style={{
            background: `url('${backgroundBenchmark}')`,
            clipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
            WebkitClipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
          }}
        ></div>
      </div>

      {/* CONTENT */}
      {program && (sharedCoach || isAuthor) ? (
        <div className="px-6">
          {/* TEXT */}
          <div className="mt-4 flex w-full flex-col items-center justify-center px-2 text-center">
            {isAuthor || isAcceptedSharedCoach ? (
              <>
                <Text fontStyle="TM" className="mt-2 text-textDefault-body">
                  {isAuthor ? word("invitationDetailsInfo.owner.title") : word("invitationDetailsInfo.coach.title")}
                </Text>
                <Text fontStyle="BM" className="mt-2 text-textDefault-body">
                  {isAuthor
                    ? word("invitationDetailsInfo.owner.subtitle")
                    : word("invitationDetailsInfo.coach.subtitle")}
                </Text>
              </>
            ) : (
              <div className="mt-5 mb-3 flex w-full flex-col items-center justify-center gap-4 text-center">
                <Text fontStyle="TL" className="text-textDefault-caption">
                  {word("invitationDetailsInfo.title", {
                    programName: program.name ?? "",
                  })}
                </Text>

                <Text fontStyle="BM" className="px-3 text-textDefault-body">
                  {word("invitationDetailsInfo.text")}
                </Text>
              </div>
            )}
          </div>
          {/* PROGRAM */}
          <div className="mt-4 flex flex-col">
            <Text className="text-textDefault-body" fontStyle="LS">
              {word("invitationDetailsInfo.relevantProgram").toUpperCase()}
            </Text>
            <div className="mt-2 flex items-center justify-center">
              {isLoading ? (
                <Skeleton className="mt-1 h-75 w-80 rounded-4xl" />
              ) : (
                <ExploreCard
                  item={
                    program.type === ProgramType.PRO
                      ? ExploreProProgramItemEntity.fromProgram(program)
                      : ExploreProgramItemEntity.fromProgram(program)
                  }
                  disabled
                  onViewDetailsClick={() => {
                    return
                  }}
                  isSelected={false}
                />
              )}
            </div>
          </div>

          {/* PERMISSIONS  */}
          <div className="mt-4 flex flex-col">
            <HuPermissions
              title={word("invitationDetailsInfo.permissions").toUpperCase()}
              role={SharedCoachRole.STANDARD}
            />
          </div>
          {/* BUTTONS */}
          {isAuthor || isAcceptedSharedCoach ? null : (
            <div className="mt-4 flex w-full flex-row justify-between gap-3">
              <Button
                variant="outline"
                size="md"
                className="flex-1"
                onClick={() => void requestSharedCoachActions(SharedCoachRequestAction.REFUSE)}
                disabled={isLoading || isPending}
              >
                {word("invitationDetailsInfo.refuse")}
                <ErrorIcon color="currentColor" className="text-white" width={20} height={20} />
              </Button>

              <Button
                variant="outline"
                size="md"
                className="flex-1"
                onClick={() => {
                  hideSidebar()
                  localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
                }}
                disabled={isLoading || isPending}
              >
                {word("invitationDetailsInfo.answerLater")}
                <ClockOutlineIcon color="currentColor" className="text-textDefault-body" width={14} height={14} />
              </Button>

              <Button
                variant="primary"
                size="md"
                className="flex-1"
                onClick={() => void requestSharedCoachActions(SharedCoachRequestAction.ACCEPT)}
                disabled={isLoading || isPending}
              >
                {word("invitationDetailsInfo.accept")}
                <CorrectIcon color="currentColor" className="text-white" width={20} height={20} />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="px-6">
          {isLoading ? (
            <Skeleton className="mt-6 h-96 w-full rounded-4xl" />
          ) : (
            <>
              {/* GESTION OF ERROR  & LOADING*/}
              <div className="mt-6 flex flex-col items-center justify-center rounded-4xl bg-neutral-100">
                <Text fontStyle="TM" className="mt-6 text-textDefault-body">
                  {word("invitationDetailsInfo.notValid.title")}
                </Text>
                <Text fontStyle="BM" className="mt-3 px-4 text-center text-textDefault-body">
                  {word("invitationDetailsInfo.notValid.subtitle")}
                </Text>
                <div
                  className="my-8 flex h-48 w-full bg-contain bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${errorInvitationShareCoach})` }}
                ></div>
                {program.owner && (
                  <Button variant="outline" size="lg" className="mb-4" onClick={openChatWithCoach}>
                    {word("invitationDetailsInfo.sendMessageToCoach")}
                    <MessagesIcon color="currentColor" className="text-textDefault-body" width={16} height={16} />
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
